import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Check, X, Reply, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function StaffMessages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [counterStart, setCounterStart] = useState("");
  const [counterEnd, setCounterEnd] = useState("");

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
  };

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!activeUserId) return;
    api.get(`/messages?with_user_id=${activeUserId}`).then((r) => {
      setMessages(r.data.reverse());
      // Mark inbound unread as read
      const unread = r.data.filter((m) => m.to_user_id === user.id && m.status === "unread");
      for (const m of unread) {
        api.post(`/messages/${m.id}/read`).catch(() => {});
      }
    });
  }, [activeUserId, user]);

  const send = async () => {
    if (!reply.trim() || !activeUserId) return;
    try {
      await api.post("/messages", { to_user_id: activeUserId, body: reply, message_type: "normal" });
      setReply("");
      const { data } = await api.get(`/messages?with_user_id=${activeUserId}`);
      setMessages(data.reverse());
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const respondAction = async (msgId, action) => {
    try {
      await api.post(`/messages/${msgId}/action`, {
        action,
        comment: action === "counter_offer" ? "代替時間を提案します" : null,
        counter_start_time: action === "counter_offer" ? counterStart : null,
        counter_end_time: action === "counter_offer" ? counterEnd : null,
      });
      toast.success("返信しました");
      const { data } = await api.get(`/messages?with_user_id=${activeUserId}`);
      setMessages(data.reverse());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (activeUserId) {
    const partner = threads.find((t) => t.with_user_id === activeUserId)?.with_user;
    return (
      <div className="p-4 max-w-md mx-auto" data-testid="staff-chat-page">
        <div className="flex items-center gap-2 mb-3">
          <Button variant="ghost" size="sm" onClick={() => setActiveUserId(null)} data-testid="back-to-threads">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-bold text-slate-900">{partner?.name || "メッセージ"}</h1>
        </div>
        <div className="space-y-2 mb-20">
          {messages.map((m) => {
            const mine = m.from_user_id === user.id;
            const isWorkReq = m.message_type === "work_request";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg p-3 text-sm ${
                  mine ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-900"
                }`}>
                  {isWorkReq && !mine && (
                    <p className="text-xs font-semibold text-orange-600 mb-1">出勤の検討依頼</p>
                  )}
                  {m.related_date && (
                    <p className="text-xs opacity-80 mb-1">対象日: {m.related_date}</p>
                  )}
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  {isWorkReq && !mine && (!m.actions || m.actions.length === 0) && (
                    <div className="mt-2 space-y-2">
                      <div className="flex gap-2">
                        <Button size="sm" data-testid={`accept-${m.id}`} className="bg-green-600 hover:bg-green-700" onClick={() => respondAction(m.id, "accept")}>
                          <Check className="w-3.5 h-3.5 mr-1" />承諾
                        </Button>
                        <Button size="sm" data-testid={`reject-${m.id}`} variant="destructive" onClick={() => respondAction(m.id, "reject")}>
                          <X className="w-3.5 h-3.5 mr-1" />NG
                        </Button>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Input type="time" value={counterStart} onChange={(e) => setCounterStart(e.target.value)} className="h-8 text-xs" data-testid={`counter-start-${m.id}`} />
                        <Input type="time" value={counterEnd} onChange={(e) => setCounterEnd(e.target.value)} className="h-8 text-xs" data-testid={`counter-end-${m.id}`} />
                        <Button size="sm" variant="outline" data-testid={`counter-${m.id}`} onClick={() => respondAction(m.id, "counter_offer")}>
                          <Reply className="w-3.5 h-3.5 mr-1" />代替
                        </Button>
                      </div>
                    </div>
                  )}
                  {m.actions && m.actions.length > 0 && (
                    <div className="mt-1 text-xs italic opacity-80">
                      返信: {m.actions[0].action}{m.actions[0].counter_start_time && ` (${m.actions[0].counter_start_time}〜${m.actions[0].counter_end_time})`}
                    </div>
                  )}
                  <p className="text-[10px] opacity-60 mt-1">{m.created_at?.slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 p-3 max-w-md mx-auto">
          <div className="flex gap-2">
            <Input data-testid="message-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="メッセージを入力" />
            <Button data-testid="send-message-button" onClick={send} className="bg-blue-600 hover:bg-blue-700">送信</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-md mx-auto space-y-3" data-testid="staff-messages-page">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">メッセージ</h1>
      {threads.length === 0 && (
        <p className="text-sm text-slate-500">メッセージはまだありません</p>
      )}
      {threads.map((t) => (
        <button
          key={t.with_user_id}
          data-testid={`thread-${t.with_user_id}`}
          onClick={() => setActiveUserId(t.with_user_id)}
          className="w-full text-left bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between hover:border-blue-300"
        >
          <div>
            <p className="font-semibold text-slate-900">{t.with_user?.name}</p>
            <p className="text-xs text-slate-500 truncate max-w-[200px]">{t.last_message?.body}</p>
          </div>
          {t.unread > 0 && (
            <span className="bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5">{t.unread}</span>
          )}
        </button>
      ))}
    </div>
  );
}
